/**
 * CommandApi — methods that MUTATE data on the server.
 *
 * Counterpart to `query-api.ts` (reads). Anything that creates, updates, or
 * deletes lives here: the capture/ingest entry point used by the floating
 * quick-capture box, plus collection creation.
 */
import { apiDelete, apiPost, apiPostFormData } from "./base-query-api";
// ---------------------------------------------------------------------------
// Client-generated block ids
// ---------------------------------------------------------------------------
// Block ids are `blk_` + 24 lowercase-alphanumeric chars (see
// `@shared/types/id` — PREFIXED_ID_PATTERN). The ingest endpoint accepts a
// `clientBlockId` and persists the root block under it, so the client can know
// the id up front and attach local state (e.g. a comment) to it immediately.
const ID_ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyz";
/** Generate a valid client-side block id (`blk_[a-z0-9]{24}`). */
export function newClientBlockId() {
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    let suffix = "";
    for (let i = 0; i < bytes.length; i += 1)
        suffix += ID_ALPHABET[bytes[i] % ID_ALPHABET.length];
    return `blk_${suffix}`;
}
// ---------------------------------------------------------------------------
// Ingest (POST /v1/ingest)
// ---------------------------------------------------------------------------
/** Captures originating from the web viewer carry this channel tag. */
const VIEWER_CHANNEL = "viewer_client";
/**
 * Fire-and-forget capture. The server enqueues the ingestion pipeline and
 * returns 202 (`{ accepted: true }`) immediately — the resulting block surfaces
 * asynchronously once processed. Sends multipart when an image is attached, JSON
 * otherwise. `apiPost*` throws on any non-2xx, so a clean return means accepted.
 */
export async function ingestCapture(options) {
    const { content, image, clientBlockId, collectionId } = options;
    if (image !== undefined) {
        const form = new FormData();
        form.set("channel", VIEWER_CHANNEL);
        form.set("clientBlockId", clientBlockId);
        if (content)
            form.set("content", content);
        if (collectionId)
            form.set("collectionId", collectionId);
        // The ingest route reads attachments from the `files` field.
        const filename = image instanceof File ? image.name : "pasted-image";
        form.append("files", image, filename);
        await apiPostFormData("/ingest", form);
        return;
    }
    await apiPost("/ingest", {
        channel: VIEWER_CHANNEL,
        clientBlockId,
        content: content ?? "",
        ...(collectionId ? { collectionId } : {}),
    });
}
// ---------------------------------------------------------------------------
// Collections (POST /v1/collection/:id/create-child)
// ---------------------------------------------------------------------------
/**
 * Create a new channel (a layer-1 collection) under a group. `parentId` is the
 * group's collection id; the server places the child at the next layer down and
 * returns the persisted row. Throws on any non-2xx.
 */
export async function createChildCollection(parentId, title) {
    const res = await apiPost(`/collection/${parentId}/create-child`, { title });
    if (!res.success || !res.data) {
        throw new Error(res.error ?? "Failed to create channel");
    }
    return res.data.collection;
}
// ---------------------------------------------------------------------------
// Comments (UserAnnotation, type "comment")
// ---------------------------------------------------------------------------
/**
 * Persist a whole-block text comment to the backend. Inline `@username` tags in
 * `body` are turned into Mention rows server-side (see api MentionService).
 *
 * Alpha's comment prototype is otherwise localStorage-only; this is the bridge
 * that gives comments real persistence + mentions. Resolves on success; throws
 * on a non-2xx so callers can keep it best-effort.
 */
export async function createCommentAnnotation(blockId, body, color) {
    const res = await apiPost("/user-annotation/create", {
        type: "comment",
        blockId,
        content: { body, ...(color ? { color } : {}) },
        anchor: { type: "whole" },
    });
    if (!res.success) {
        throw new Error(res.error ?? "Failed to create comment");
    }
}
/** Soft-delete a backend UserAnnotation (e.g. a comment) by id. */
export async function deleteUserAnnotation(userAnnotationId) {
    const res = await apiDelete(`/user-annotation/${userAnnotationId}`);
    if (!res.success) {
        throw new Error(res.error ?? "Failed to delete comment");
    }
}
